import { PropsWithChildren, useRef, useState } from 'react';

import { Box, Button, Card, VisuallyHidden } from '@chakra-ui/react';

import { ListIcon } from '~/features/experiments/ab_plp_redesign_2025/icons';
import { logger } from '~/features/logging/logger';

type CopyToTextProps = PropsWithChildren<{ name: string; text: string; label: string }>;

function logCopyError(error: unknown) {
  logger.error('Error copying reimbursement email template', {
    file: 'src/features/reimbursement/components/copy-to-text.tsx',
    function: 'handleCopyText',
    error,
  });
}

export function CopyToText({ name, text, label, children }: CopyToTextProps) {
  const [copied, setCopied] = useState(false);
  const textAreaRef = useRef<HTMLTextAreaElement>(null);

  const handleCopyText = async (ev: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement, MouseEvent>) => {
    ev.preventDefault();

    if (navigator.clipboard && window.isSecureContext) {
      try {
        await navigator.clipboard.writeText(text);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch (error) {
        logCopyError(error);
      }

      return;
    }

    if (!textAreaRef.current) {
      return;
    }

    textAreaRef.current.select();

    try {
      document.execCommand('copy');
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      logCopyError(error);
    }
  };

  return (
    <Card
      backgroundColor='grey.50'
      border='1px solid'
      borderColor='grey.200'
      borderRadius='6px'
      boxShadow='none'
      display='flex'
      gap={6}
      padding={{ base: 6, md: 10 }}
      variant='outline'
    >
      <Box display='flex' flexDirection='column' gap={6} id={`${name}-content`}>
        {children}
      </Box>
      <VisuallyHidden>
        <label htmlFor={name}>{label}</label>
        <textarea ref={textAreaRef} id={name} value={text} readOnly />
      </VisuallyHidden>
      <Button
        alignSelf='flex-start'
        colorScheme='blue'
        leftIcon={<ListIcon height='18px' width='18px' />}
        variant='solid'
        width={{ base: '100%', md: '192px' }}
        onClick={handleCopyText}
      >
        {copied ? 'Copied!' : 'Copy Text'}
      </Button>
    </Card>
  );
}
